import { useState } from 'react'
import { Sheet } from '../ui/Sheet'
import { sendReport } from '../transport/relay'
import { getShellBuild } from './shellOrigin'

/**
 * Reporting content or behaviour — owner, 2026-09-15: 在 Side panel 的設定頁面來製作
 * 檢舉的功能. 我想只需要一個可以回報檢舉的欄位就夠. 不需要做的太複雜.
 *
 * WHY IT EXISTS. App Review returned 1.0 under guideline 2.1 asking to see "the
 * required content reporting and blocking mechanisms". Blocking exists — Humans →
 * Remove ends messaging both ways — and reporting was only the support address inside
 * the privacy policy, which guideline 1.2 does not accept as a mechanism.
 *
 * A BUTTON IN THE MENU, THE FIELD IN A SHEET — owner, 2026-09-16: 應該要設計成為它只是
 * 一個按鈕就好, 當使用者按下 … 會跳出來一個彈跳視窗 … 事實上, 輸入的文字欄裡面就可以放
 * 一些說明文字 … 就不用再浪費顯示空間了. The first version put the guidance and a
 * permanently-open textarea straight into the menu, which spent a third of a phone
 * screen on a control almost nobody uses on any given day. The menu now costs one
 * row; everything else moves into the sheet the press opens.
 *
 * THE PLACEHOLDER CARRIES THE PROMPT, not the policy. Hint text vanishes the moment
 * someone types, so it is the right home for "what happened?" and the wrong home for
 * the two things this screen is obliged to say: WHICH things are worth reporting, and
 * that removing someone is a different action that stops them immediately. Those stay
 * visible, compressed into one block — a person who has just been sent something
 * horrible should not have to decide whether it qualifies, and should not confuse
 * reporting with blocking.
 *
 * TWO DOORS, ONE FIELD. `ReportForm` is the whole mechanism and is also rendered by a
 * person's own sheet (FriendsScreen) beside Remove. Reviewers look for reporting AT
 * the content, not only in settings — a form reachable only from the bottom of a menu
 * invites "we were unable to locate the reporting mechanism".
 *
 * THE CONFIRMATION MAKES NO TIME PROMISE. The first version said "within 24 hours",
 * which is a commitment a reviewer can test and a user can rely on, and nothing on our
 * side guarantees it: the row is durable, the human who reads it is not on a rota.
 * avg-devapp-appstore ruled the weaker true sentence, so the app and the reply to
 * Apple say the same thing. Printed only on a confirmed send, never on a failure.
 */

/** What is worth reporting, and what blocking does instead. Both doors say this. */
export function ReportGuidance() {
  return (
    <p className="gh-note">
      Report content or behaviour that is abusive, harassing, hateful, sexual, violent,
      illegal, a scam, or someone pretending to be another person. To stop someone
      contacting you straight away, remove them under <strong>Humans</strong> — that is
      immediate and separate from reporting, and you can do both.
    </p>
  )
}

export function ReportForm({ about, aboutLabel }: { about?: string; aboutLabel?: string }) {
  const [text, setText] = useState('')
  const [state, setState] = useState<'idle' | 'sending' | 'sent'>('idle')
  const [error, setError] = useState('')

  const submit = async () => {
    if (state === 'sending') return
    setError('')
    setState('sending')
    const build = getShellBuild()
    const r = await sendReport({
      text,
      ...(about ? { about } : {}),
      ...(build ? { appBuild: build } : {}),
    })
    if (r.ok) {
      // Cleared only on success. A failed send that wiped the field would make the
      // person type it all again — after they have already been upset once.
      setText('')
      setState('sent')
      return
    }
    setState('idle')
    setError(r.message)
  }

  if (state === 'sent') {
    return (
      <>
        <p className="gh-note" role="status">
          Thank you — we have your report. We review every report.
        </p>
        <button type="button" className="gh-mini" onClick={() => setState('idle')}>
          Send another report
        </button>
      </>
    )
  }

  return (
    <>
      {aboutLabel && (
        // Who this report is about, said on screen as well as carried in `about` —
        // the person should be able to see they are reporting the right account
        // before they send, not trust that the button knew.
        <p className="gh-note">
          About <strong>{aboutLabel}</strong>.
        </p>
      )}
      <textarea
        className="gh-report"
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder="What happened? Tell us as much as you can, and name the person or project if you know it."
        rows={5}
        aria-label="Describe the problem you are reporting"
      />
      {error && (
        <p className="gh-note gh-report-error" role="alert">
          {error}
        </p>
      )}
      <button
        type="button"
        className="gh-mini"
        /* Disabled on an empty field rather than letting the server refuse it: the
           round trip would tell the person off for something the screen could have
           told them, and the server still refuses it either way. */
        disabled={!text.trim() || state === 'sending'}
        onClick={() => void submit()}
      >
        {state === 'sending' ? 'Sending…' : 'Send report'}
      </button>
    </>
  )
}

/**
 * ONE BUTTON. Owner, 2026-09-16, on seeing the first attempt at this: 應該要把整個
 * 東西改成一個簡單的按鈕就好 … 你把它放在這個設定選單裡面, 非常的佔空間, 而且並不是
 * 所有的使用者, 時時刻刻打開選單都會想要 report a problem.
 *
 * The version before this still cost three rows — a section heading, a label, and a
 * small button beside it — which is two rows more than a control most people never
 * touch deserves in a menu they open for something else. No heading: a standalone
 * control does not take one here (DeleteAccountButton below is the same shape).
 */
export function ReportSection() {
  const [open, setOpen] = useState(false)
  return (
    <>
      <button type="button" className="gh-report-open" onClick={() => setOpen(true)}>
        Report a problem
      </button>
      {open && (
        <Sheet title="Report a problem" onClose={() => setOpen(false)}>
          <ReportGuidance />
          <ReportForm />
        </Sheet>
      )}
    </>
  )
}
